/**
 * Loading, empty, and error states.
 *
 * These are shared rather than per-screen because consistency matters more than
 * novelty here: a merchant glancing at the counter iPad should recognise a
 * failure the same way in every panel.
 *
 * Every tone maps to a whole literal class string. Tailwind reads class names
 * from source text, so a name assembled at runtime would never be generated; see
 * `TONE_SOFT` and its siblings in `lib/theme.ts`.
 */

import type { ReactNode } from 'react';
import { ActivityIndicator, View } from 'react-native';

import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Inbox,
  Info,
  Star,
  type LucideIcon,
} from 'lucide-react-native';

import { Button } from './ui/button';
import { Card } from './ui/card';
import { Icon } from './ui/icon';
import { Skeleton as SkeletonPrimitive } from './ui/skeleton';
import { Text } from './ui/text';
import { Row, Stack } from './layout';
import { useAppearance } from '../providers/appearance';
import { useT } from '../providers/locale';
import { cn } from '../lib/utils';
import { TONE_SOFT, TONE_SOLID, TONE_TEXT, type Tone } from '../lib/theme';

/** Icon shown when a callout is given none. */
const CALLOUT_ICON: Record<Tone, LucideIcon> = {
  neutral: Info,
  brand: Star,
  success: CheckCircle2,
  info: Info,
  progress: CircleDashed,
  warning: AlertTriangle,
  danger: AlertTriangle,
};

/** The solid tone rule down a callout's leading edge. */
const CALLOUT_RULE: Record<Tone, string> = {
  neutral: 'border-l-subtle-foreground',
  brand: 'border-l-primary',
  success: 'border-l-success',
  info: 'border-l-info',
  progress: 'border-l-progress',
  warning: 'border-l-warning',
  danger: 'border-l-destructive',
};

/**
 * A centred spinner.
 *
 * @param props Optional label and size.
 * @returns The spinner, with its label beneath it.
 */
export function Spinner({ size = 'large', label }: { size?: 'small' | 'large'; label?: string }) {
  const { palette } = useAppearance();

  return (
    <Stack gap="md" align="center" justify="center" className="p-6">
      <ActivityIndicator size={size} color={palette.primary} />
      {label ? <Text className="text-muted-foreground text-xs">{label}</Text> : null}
    </Stack>
  );
}

/**
 * A full-panel loading placeholder.
 *
 * @param props Message to show beneath the spinner.
 * @returns The spinner reading the message.
 */
export function LoadingState({ message = 'Loading…' }: { message?: string }) {
  return <Spinner label={message} />;
}

/**
 * A full-panel empty state with an optional action.
 *
 * @param props Icon, title, explanation, and action.
 * @returns The centred empty state.
 */
export function EmptyState({
  icon: IconComponent = Inbox,
  title,
  message,
  actionLabel,
  onAction,
}: {
  /** The mark above the title; defaults to an empty tray. */
  icon?: LucideIcon;
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <Stack gap="sm" align="center" className="p-8">
      <Icon
        as={IconComponent}
        size={32}
        className="text-subtle-foreground"
        strokeWidth={1.5}
        aria-hidden
      />
      <Text className="text-center text-xl font-semibold">{title}</Text>
      {message ? (
        <Text className="text-muted-foreground max-w-[420px] text-center text-base">{message}</Text>
      ) : null}
      {actionLabel && onAction ? (
        <View className="mt-3">
          <Button variant="secondary" onPress={onAction}>
            <Text>{actionLabel}</Text>
          </Button>
        </View>
      ) : null}
    </Stack>
  );
}

/**
 * An inline message block.
 *
 * @param props Tone, title, message, and optional action.
 * @returns The tinted block, with the tone's solid colour on its leading edge.
 */
export function Callout({
  tone = 'info',
  title,
  message,
  action,
  icon,
  size = 'sm',
}: {
  tone?: Tone;
  title?: string;
  message: string;
  action?: ReactNode;
  /** Overrides the tone's own mark. */
  icon?: LucideIcon;
  /**
   * How the message is set.
   *
   * `sm` is 12pt and `base` is 14, the size of the body text around it. Opt-in
   * rather than the default: the customer app's callouts are the answer to the
   * question the screen asks, while the merchant's are notes inside dense
   * panels where the extra line costs more than the extra point buys.
   */
  size?: 'sm' | 'base';
}) {
  return (
    <View className={cn('rounded-md border-l-[3px] p-3', TONE_SOFT[tone], CALLOUT_RULE[tone])}>
      <Row gap="sm" align="start">
        <Icon
          as={icon ?? CALLOUT_ICON[tone]}
          size={16}
          strokeWidth={2.5}
          className={cn('mt-0.5', TONE_TEXT[tone])}
          aria-hidden
        />
        <Stack gap="xs" className="flex-1">
          {title ? (
            <Text className={cn('text-base font-bold', TONE_TEXT[tone])}>{title}</Text>
          ) : null}
          <Text className={cn(size === 'base' ? 'text-sm' : 'text-xs', TONE_TEXT[tone])}>
            {message}
          </Text>
          {action}
        </Stack>
      </Row>
    </View>
  );
}

/**
 * A compact error banner with an optional retry.
 *
 * @param props The error message and a retry handler.
 * @returns The banner, flagged by a destructive leading edge.
 */
export function ErrorBanner({
  message,
  onRetry,
  title,
}: {
  message: string;
  onRetry?: () => void;
  title?: string;
}) {
  const t = useT();

  return (
    <Card className="border-l-destructive gap-2 border-l-[3px] p-3">
      <Stack gap="sm">
        <Text className="text-destructive text-base font-bold">
          {title ?? t('common.somethingWentWrong')}
        </Text>
        <Text className="text-muted-foreground text-xs">{message}</Text>
        {onRetry ? (
          <View className="self-start">
            <Button variant="secondary" size="sm" onPress={onRetry}>
              <Text>{t('common.retry')}</Text>
            </Button>
          </View>
        ) : null}
      </Stack>
    </Card>
  );
}

/**
 * A placeholder block for content that is still arriving.
 *
 * Static rather than animated: a subtle grey block communicates the same thing
 * without an animation loop competing with the countdown timers on the same
 * screen. Size is a measured value, so it travels through `style` rather than a
 * class.
 *
 * @param props Height, width, and corner radius.
 * @returns The grey block.
 */
export function Skeleton({
  height = 16,
  width = '100%',
  radius,
}: {
  height?: number;
  width?: number | `${number}%`;
  radius?: number;
}) {
  return (
    <SkeletonPrimitive
      className="bg-muted animate-none rounded-sm"
      style={radius === undefined ? { height, width } : { height, width, borderRadius: radius }}
    />
  );
}

/**
 * A horizontal progress bar.
 *
 * `Progress` takes a 0–100 `value` and has no tone or height props, so this
 * keeps the track and the fill as two views and computes the fill's width — the
 * one value here that a class cannot carry.
 *
 * @param props Progress from 0 to 1, optional tone, and height.
 * @returns The bar, exposed to assistive technology as a percentage.
 */
export function ProgressBar({
  progress,
  tone = 'brand',
  height = 8,
}: {
  progress: number;
  tone?: Tone;
  height?: number;
}) {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(progress) ? progress : 0));

  return (
    <View
      className="bg-muted w-full overflow-hidden rounded-full"
      style={{ height }}
      accessibilityRole="progressbar"
      // `aria-value*`, not `accessibilityValue`: react-native-web reads the
      // `aria-*` props and ignores the object, so the bar announced nothing.
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped * 100)}>
      <View
        className={cn('h-full rounded-full', TONE_SOLID[tone])}
        style={{ width: `${clamped * 100}%` }}
      />
    </View>
  );
}
